<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePerfilUsuarioStore } from '@/stores/perfilUsuario'
import { useNotificationStore } from '@/stores/notification'

const router = useRouter()
const route = useRoute()
const perfilUsuarioStore = usePerfilUsuarioStore()
const notificationStore = useNotificationStore()

const perfil = ref({
  descricao: '',
  nivel: '',
})

const isEditMode = ref(false)
const error = ref('')
const loading = ref(false)

const perPage = ref(50)
const page = ref(1)

// nova função para carregar perfis de forma assincrona
const fetchPaginasPaginated = async () => {
  try {
    const id = route.params.id

    loading.value = true
    await perfilUsuarioStore.fetchPaginas(id, { page: page.value, perPage: perPage.value })
  } catch {
    notificationStore.showNotification('Erro ao carregar perfis', 'error')
  } finally {
    loading.value = false
  }
}

// substitui o watch anterior para usar a função async (mesma abordagem do MensagensClienteView)
watch([perPage, page], () => {
  fetchPaginasPaginated()
})

onMounted(async () => {
  const id = route.params.id
  if (id) {
    isEditMode.value = true
    const fetchedPerfil = await perfilUsuarioStore.fetchPerfilById(id)
    perfil.value = fetchedPerfil || perfil.value
    await perfilUsuarioStore.fetchPaginas(id, { page: page.value, perPage: perPage.value })
  }
})

const savePerfil = async () => {
  try {
    loading.value = true
    if (isEditMode.value) {
      await perfilUsuarioStore.updatePerfil(perfil.value)
      notificationStore.showNotification('Perfil atualizado com sucesso!', 'success')
    } else {
      await perfilUsuarioStore.createPerfil(perfil.value)
      notificationStore.showNotification('Perfil criado com sucesso!', 'success')
    }
    //router.push('/perfis-usuario')
  } catch (error) {
    notificationStore.showNotification('Erro ao salvar perfil!', 'error')
  } finally {
    loading.value = false
  }
}

const handlePaginaEstadoChange = async (pagina) => {
  try {
    await perfilUsuarioStore.updatePaginaEstado(route.params.id, pagina.id, pagina.estado ? 1 : 0)
    if (!pagina.estado) {
      notificationStore.showNotification('Página removida com sucesso!', 'success')
    } else {
      notificationStore.showNotification('Página adicionada com sucesso!', 'success')
    }
  } catch (err) {
    notificationStore.showNotification('Erro ao atualizar estado da página', 'error')
  }
}

const handleBack = () => {
  router.push('/perfis');
};

const paginasComputed = computed(() => {
  return perfilUsuarioStore.paginas.map(pagina => ({
    ...pagina,
    estado: pagina.estado === 1,
  }))
})

// adiciona computed para meta da store (usado pela paginação)
const meta = computed(() => perfilUsuarioStore.meta || { current_page: 1, last_page: 1, total: 0 })

</script>

<template>
  <v-card class="elevation-12">
    <v-card-title class="text-left">
      <h1 class="text-h5 mt-4">{{ isEditMode ? 'Editar Perfil de Usuário' : 'Cadastrar Perfil de Usuário' }}</h1>
    </v-card-title>

    <v-card-text>
      <v-alert v-if="error" type="error" variant="tonal" closable class="mb-4" @click:close="error = ''">
        {{ error }}
      </v-alert>

      <v-form @submit.prevent="savePerfil">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="perfil.descricao" label="Descrição" required hide-details="auto"></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field v-model="perfil.nivel" label="Nível" required hide-details="auto"></v-text-field>
          </v-col>
        </v-row>

        <v-row v-if="isEditMode">
          <v-col cols="12">
            <h3 class="text-h6">Gerenciar acesso a páginas</h3>

            <v-progress-linear v-if="loading" indeterminate color="primary" size="20" :width="2"></v-progress-linear>

            <v-data-table :headers="[
              { title: 'Estado', value: 'estado' },
              { title: 'Página', value: 'name' },
            ]" :items="paginasComputed" :items-per-page="perPage">
              <template #item="{ item }">
                <tr>
                  <td>
                    <v-switch hide-details="auto" v-model="item.estado" label="" color="success"
                      @change="handlePaginaEstadoChange(item)"></v-switch>
                  </td>
                  <td>{{ item.name }}</td>
                </tr>
              </template>

              <!-- adicionado: rodapé com total, select de itens por página e paginação -->
              <template #bottom>
                <div class="d-flex justify-space-between align-center px-4 py-2">
                  <div class="d-flex align-center">
                    <span class="text-caption text-secondary mr-4">
                      Total de {{ meta.total || paginasComputed.length }} item{{ (meta.total || 0) > 1 ? 's' : '' }}
                    </span>

                    <v-select v-model="perPage" :items="[10, 25, 50, 100]" label="Itens por página" variant="outlined"
                      dense hide-details style="width: 140px" />
                  </div>

                  <v-pagination v-if="meta.last_page > 1" :model-value="page" :length="meta.last_page"
                    :total-visible="5" @update:model-value="(newPage) => page = newPage" rounded
                    class="my-2"></v-pagination>
                </div>
              </template>

            </v-data-table>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-btn color="primary" block size="large" @click="savePerfil" :loading="loading">
              {{ isEditMode ? 'Salvar' : 'Cadastrar' }}
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn variant="text" color="primary" block @click="handleBack()">Voltar</v-btn>
          </v-col>
        </v-row>

      </v-form>
    </v-card-text>
  </v-card>
</template>

<style scoped></style>
